<template>
  <div class="flex flex-col h-full w-full bg-soft" data-testid="tool-editor">
    <header class="flex-shrink-0 pl-14 pr-10 py-6 bg-white border-solid border-b border-0 border-soft">
      <h3 class="font-semibold text-gray-dark leading-tight">
        {{ isNew ? 'Add tool' : draft.name }}
      </h3>
      <p class="mt-1 text-sm text-gray">
        The AI reads the name and description to decide when to call this. The request is what it calls.
      </p>
    </header>

    <perfect-scrollbar class="flex-1 w-full tool-editor-body">
      <div class="w-full min-w-0 max-w-3xl pl-14 pr-10 py-8 flex flex-col gap-6">

        <section class="bg-white rounded-lg border border-solid border-soft p-6">
          <h4 class="font-semibold text-gray-dark">Configuration</h4>
          <p class="mt-1 text-sm text-gray">Describe to the AI how and when to use the tool.</p>

          <div class="mt-5 flex flex-col">
            <label for="toolName" class="text-sm font-medium text-gray-dark mb-2">Name</label>
            <base-input id="toolName"
                        v-model="draft.name"
                        data-testid="tool-name-input"
                        class="font-mono placeholder-gray"
                        placeholder="get_order_status"
            />
            <span class="mt-2 text-xs text-gray">Lowercase letters, digits and underscores.</span>
          </div>

          <div class="mt-5 flex flex-col">
            <label for="toolDescription" class="text-sm font-medium text-gray-dark mb-2">Description</label>
            <t-textarea id="toolDescription"
                        v-model="draft.description"
                        data-testid="tool-description-input"
                        variant="textSection"
                        rows="3"
                        placeholder="Look up the delivery status of an order once the customer gives an order number."
            />
            <span class="mt-2 text-xs text-gray">
              Be specific — this is how the AI tells this tool apart from the others. Keep it about
              <em>when</em> to call the tool; what to do with the answer belongs in Response handling below.
            </span>
          </div>
        </section>

        <section class="bg-white rounded-lg border border-solid border-soft p-6">
          <h4 class="font-semibold text-gray-dark">Parameters</h4>
          <p class="mt-1 text-sm text-gray">
            Values the AI works out from the conversation. Use one as
            <code class="text-gray-dark">${{ '{' }}args.name}</code> anywhere in the request.
          </p>

          <tool-parameters-editor v-model="draft.parameters" class="mt-5"/>
        </section>

        <section class="bg-white rounded-lg border border-solid border-soft p-6">
          <h4 class="font-semibold text-gray-dark">Request</h4>
          <p class="mt-1 text-sm text-gray">What the AI actually calls when it decides to use this tool.</p>

          <div class="mt-5 flex gap-3">
            <div class="w-32 flex-shrink-0 flex flex-col">
              <label class="text-sm font-medium text-gray-dark mb-2">Method</label>
              <base-select v-model="draft.http_verb"
                           :options="httpVerbOptions"
                           data-testid="tool-http-verb-select"
                           class="h-10 font-semibold"
              />
            </div>
            <div class="flex-1 min-w-0 flex flex-col">
              <label for="toolUrl" class="text-sm font-medium text-gray-dark mb-2">URL</label>
              <base-input id="toolUrl"
                          v-model="draft.url"
                          data-testid="tool-url-input"
                          class="font-mono placeholder-gray"
                          placeholder="https://api.example.com/orders/${args.orderId}"
              />
            </div>
          </div>
          <span class="block mt-2 text-xs text-gray">
            <code class="text-gray-dark">${{ '{' }}args.name}</code> is filled by the AI.
            <code class="text-gray-dark">${{ '{' }}variable.name}</code> is filled per bot and market — the domain, an API key.
          </span>

          <div class="mt-5">
            <base-toggle-group :tabs="requestTabOptions" v-model="requestTab"/>
            <json-input v-if="requestTab === TABS.HEADERS" class="mt-3" v-model="headersJson"/>
            <json-input v-else-if="requestTab === TABS.QUERY" class="mt-3" v-model="queryParamsJson"/>
            <t-textarea v-else
                        class="mt-3 font-mono"
                        v-model="draft.body"
                        variant="textSection"
                        rows="4"
                        placeholder='{"orderId": "${args.orderId}"}'
            />
          </div>

          <div class="mt-5 flex flex-col w-48">
            <label for="toolTimeout" class="text-sm font-medium text-gray-dark mb-2">Give up after</label>
            <base-input id="toolTimeout"
                        v-model.number="draft.timeout_ms"
                        data-testid="tool-timeout-input"
            />
            <span class="mt-2 text-xs text-gray">Milliseconds. The customer is waiting.</span>
          </div>
        </section>

        <section class="bg-white rounded-lg border border-solid border-soft p-6">
          <h4 class="font-semibold text-gray-dark">Response handling</h4>
          <p class="mt-1 text-sm text-gray">
            Optional. Read by the AI only after this tool has run, so go through every outcome the API can
            return — including a failure — and say what the customer should hear for each.
          </p>

          <t-textarea id="toolOutcomeInstructions"
                      v-model="draft.outcome_instructions"
                      data-testid="tool-outcome-instructions-input"
                      variant="textSection"
                      rows="8"
                      class="mt-5"
                      placeholder="If status is DELIVERED, give the delivery date.
If status is RETURN_REJECTED, explain the reason and offer an exchange.
If the request fails, apologise and hand over to an agent."
          />
          <span class="mt-2 block text-xs text-gray">Up to 8000 characters.</span>
        </section>

        <section v-if="!isNew" class="bg-white rounded-lg border border-solid border-soft p-6">
          <h4 class="font-semibold text-gray-dark">Try it</h4>
          <p class="mt-1 text-sm text-gray">
            Fill in the parameters the AI would work out, and send the request. You get the API's raw
            response — not an AI answer.
            <span v-if="hasUnsavedChanges" class="text-focus-warning">Runs the saved version, not your unsaved edits.</span>
          </p>

          <tool-test-panel :key="tool.id" :tool="tool" class="mt-5"/>
        </section>
      </div>
    </perfect-scrollbar>

    <footer class="flex-shrink-0 pl-14 pr-10 py-4 bg-white flex items-center gap-3 border-solid border-t border-0 border-soft">
      <busy-button id="tool-save-button"
                   data-testid="tool-save"
                   variant="primary"
                   :is-loading="isSaving"
                   :is-disabled="!canSave"
                   @click="save"
      >
        {{ isNew ? 'Add tool' : 'Save changes' }}
      </busy-button>
      <base-button variant="bordered" class="h-10 px-5" data-testid="tool-cancel" @click="$emit('cancel')">
        Cancel
      </base-button>
    </footer>
  </div>
</template>

<script>
import BaseButton from '@/components/base-components/BaseButton.vue'
import BusyButton from '@/components/common/BusyButton.vue'
import JsonInput from '@/components/base-components/JsonInput.vue'
import ToolParametersEditor from '@/components/tools/ToolParametersEditor.vue'
import ToolTestPanel from '@/components/tools/ToolTestPanel.vue'
import clone from 'ramda/src/clone'
import toolsModule from '@/store/modules/tools.module'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'

const TABS = { HEADERS: 'headers', BODY: 'body', QUERY: 'query' }
const HTTP_VERBS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

export default {
  name: 'ToolEditor',

  // JsonInput and the common components are not Base*-prefixed, so they do not auto-register
  components: { BaseButton, BusyButton, JsonInput, PerfectScrollbar, ToolParametersEditor, ToolTestPanel },

  props: {
    tool: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      TABS,
      isSaving: false,
      requestTab: TABS.HEADERS,
      // BaseToggleGroup reads `key`/`label` — `text`/`value` renders an empty bar with no tabs
      requestTabOptions: [
        { key: TABS.HEADERS, label: 'Headers' },
        { key: TABS.BODY, label: 'Body' },
        { key: TABS.QUERY, label: 'Query params' }
      ],
      httpVerbOptions: HTTP_VERBS.map(verb => ({ text: verb, value: verb })),
      // A working copy, so Cancel really discards and the list keeps showing the saved values.
      draft: buildDraft(this.tool)
    }
  },

  computed: {
    isNew() {
      return !this.tool.id
    },

    // The test endpoint runs the STORED tool, so say so plainly when the form has moved on from it.
    hasUnsavedChanges() {
      return JSON.stringify(buildDraft(this.tool)) !== JSON.stringify(this.draft)
    },

    // Mirrors only what the API makes REQUIRED. Notably the ${args.x}-must-be-declared rule is NOT
    // duplicated here: one implementation, in the engine, is the one that has to be right — and its
    // message is shown as a toast.
    canSave() {
      return !!(this.draft.name && this.draft.description && this.draft.url)
    },

    headersJson: {
      get() { return this.draft.headers || {} },
      set(value) { this.draft.headers = value }
    },

    queryParamsJson: {
      get() { return this.draft.query_params || {} },
      set(value) { this.draft.query_params = value }
    }
  },

  methods: {
    async save() {
      this.isSaving = true
      const saved = await toolsModule.saveTool({ ...this.draft, id: this.tool.id })
      this.isSaving = false
      // null means the API rejected it; the drawer stays open with the input intact.
      if (saved) this.$emit('saved', saved)
    }
  }
}

function buildDraft(tool) {
  return {
    name: tool.name || '',
    description: tool.description || '',
    outcome_instructions: tool.outcome_instructions || '',
    parameters: clone(tool.parameters) || { type: 'object', properties: {}, required: [], additionalProperties: false },
    url: tool.url || '',
    http_verb: tool.http_verb || 'GET',
    headers: clone(tool.headers) || {},
    body: tool.body || '',
    query_params: clone(tool.query_params) || {},
    timeout_ms: tool.timeout_ms || 10000
  }
}
</script>

<style lang="scss" scoped>
.tool-editor-body {
  min-height: 0;
  // the drawer can be narrower than the content's natural width; without this the sections
  // overflow horizontally and the URL / description inputs get clipped instead of shrinking
  max-width: 100%;
}
</style>
